import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useBridgeDataInterface } from './bridge-data-interface';
import type { CommerceComponentsDataInterfaceWithAuthentication } from './bridge-data-interface';

type CommerceBridgeContextValue = CommerceComponentsDataInterfaceWithAuthentication | null | undefined;

// null means there is no host provider (or its graph failed); undefined means a host provider is
// present but intentionally disabled or has not published its first committed value yet.
export const CommerceBridgeContext = createContext<CommerceBridgeContextValue>(null);

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type CommerceBridgeProviderProps = {
  children: ReactNode;
  enabled?: boolean;
  resetKeys?: Array<unknown>;
};

type CommerceBridgeDataSourceProps = {
  publish: (value: CommerceBridgeContextValue) => void;
};

const CommerceBridgeDataSource: React.FC<CommerceBridgeDataSourceProps> = ({ publish }) => {
  const bridgeData = useBridgeDataInterface();

  useIsomorphicLayoutEffect(() => {
    publish(bridgeData);
  }, [bridgeData, publish]);

  useIsomorphicLayoutEffect(
    () => () => {
      publish(undefined);
    },
    [publish]
  );

  return null;
};

const CommerceBridgeErrorSignal: React.FC<CommerceBridgeDataSourceProps> = ({ publish }) => {
  useIsomorphicLayoutEffect(() => {
    publish(null);
    return () => {
      publish(undefined);
    };
  }, [publish]);

  return null;
};

export const CommerceBridgeProvider: React.FC<CommerceBridgeProviderProps> = ({
  children,
  enabled = true,
  resetKeys,
}) => {
  const [bridgeData, setBridgeData] = useState<CommerceBridgeContextValue>(undefined);
  const publish = useCallback((value: CommerceBridgeContextValue) => setBridgeData(value), []);

  return (
    <CommerceBridgeContext.Provider value={bridgeData}>
      {enabled ? (
        <ErrorBoundary
          resetKeys={resetKeys}
          fallbackRender={({ error }) => {
            console.error('Commerce bridge provider error:', error);
            return <CommerceBridgeErrorSignal publish={publish} />;
          }}
        >
          <CommerceBridgeDataSource publish={publish} />
        </ErrorBoundary>
      ) : null}
      <React.Fragment key='commerce-bridge-content'>{children}</React.Fragment>
    </CommerceBridgeContext.Provider>
  );
};

export const useCommerceBridge = (): CommerceBridgeContextValue => useContext(CommerceBridgeContext);
